/**
 * Local identifier allocation for the editor.
 *
 * Mirrors `IdentifierScope.allocate` in backend/fluidea/core/identity.py:
 * `<stem>_<n>`, taking the lowest ordinal not live *or retired*. Skipping
 * retired names is the rule that matters (invariant 2, requirement 7.5): a new
 * valve must never inherit `valve_1` from a deleted one, or an old reference
 * would resolve — to the wrong element, silently.
 *
 * The backend schema rejects a document that reuses a retired identifier, so a
 * mistake here fails the save rather than corrupting a design.
 */
import { regionApplications, type DesignDocument } from "../design/document";

/** `Mixing Channel` -> `mixing_channel`, as `normalise_role` does. */
export function normaliseRole(role: string): string {
  const stem = role
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!stem) {
    throw new Error(`role ${JSON.stringify(role)} does not yield an identifier stem`);
  }
  return /^[0-9]/.test(stem) ? `n_${stem}` : stem;
}

/**
 * Every identifier a new element must not take: those in use, those retired,
 * and `reserved` — the published base version's names the draft lacks, which
 * will be retired at the next publish (D29).
 */
export function usedIdentifiers(design: DesignDocument, reserved: Iterable<string> = []): Set<string> {
  return new Set([
    ...reserved,
    ...(design.nodes ?? []).map((element) => element.local_id),
    ...(design.edges ?? []).map((element) => element.local_id),
    ...(design.group_instances ?? []).map((element) => element.local_id),
    ...regionApplications(design).map((element) => element.local_id),
    ...(design.text_notes ?? []).map((element) => element.local_id),
    ...(design.retired_local_ids ?? []),
  ]);
}

/** The next free identifier for a role. Adds it to `used`, so calls can chain. */
export function allocateIdentifier(role: string, used: Set<string>): string {
  const stem = normaliseRole(role);
  let ordinal = 1;
  while (used.has(`${stem}_${ordinal}`)) {
    ordinal += 1;
  }
  const identifier = `${stem}_${ordinal}`;
  used.add(identifier);
  return identifier;
}
